import React, { useState } from 'react';
import { Table, Button, Tag, Space, Input, Card, Empty, Typography, Rate, Modal, Divider } from 'antd';
import { SearchOutlined, StarOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

interface ReceivedReference {
  id: number;
  content: string;
  rating: number;
  title?: string;
  createdAt: string;
  writerName: string;
  writerId: number;
  mentorBookingId?: number;
  bootcampApplicationId?: number;
}

const ReceivedReferencesTab: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedReference, setSelectedReference] = useState<ReceivedReference | null>(null);

  const { data: references = [], isLoading: loading, refetch: loadReferences } = useQuery({
    queryKey: mentorProfileQueryKeys.receivedReferences,
    queryFn: async () => {
      const res = await apiService.getMentorReceivedReferences();
      if (!res.success) throw new Error(res.message || 'Referanslar yüklenemedi.');
      return res.data;
    },
  });

  const getTypeText = (mentorBookingId?: number, bootcampApplicationId?: number) => {
    if (mentorBookingId) return '1-1 Görüşme';
    if (bootcampApplicationId) return 'Bootcamp';
    return 'Bilinmiyor';
  };

  const getTypeColor = (mentorBookingId?: number, bootcampApplicationId?: number) => {
    if (mentorBookingId) return 'blue';
    if (bootcampApplicationId) return 'green';
    return 'default';
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'var(--success)';
    if (rating >= 3) return '#faad14';
    return 'var(--error)';
  };

  const handleViewDetail = (record: ReceivedReference) => {
    setSelectedReference(record);
    setDetailModalVisible(true);
  };

  const handleModalClose = () => {
    setDetailModalVisible(false);
    setSelectedReference(null);
  };

  const filteredReferences = references.filter(reference =>
    reference.writerName.toLowerCase().includes(searchText.toLowerCase()) ||
    (reference.title && reference.title.toLowerCase().includes(searchText.toLowerCase())) ||
    reference.content.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Yazan',
      dataIndex: 'writerName',
      key: 'writerName',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => (
        <Text strong style={{ color: 'var(--primary-500)' }}>
          {title || 'Başlık yok'}
        </Text>
      )
    },
    {
      title: 'Puan',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number) => (
        <Space>
          <Rate 
            disabled 
            value={rating} 
            style={{ fontSize: '16px' }}
          />
          <Text style={{ color: getRatingColor(rating), fontWeight: 'bold' }}>
            {rating}/5
          </Text>
        </Space>
      )
    },
    {
      title: 'Tip',
      key: 'type',
      render: (record: ReceivedReference) => (
        <Tag color={getTypeColor(record.mentorBookingId, record.bootcampApplicationId)}>
          {getTypeText(record.mentorBookingId, record.bootcampApplicationId)}
        </Tag>
      )
    },
    {
      title: 'Referans İçeriği',
      dataIndex: 'content',
      key: 'content',
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 200 }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: ReceivedReference) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            Detay
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0 }}>Bana Yazılan Referanslar</Title>
          <Button onClick={() => void loadReferences()} loading={loading}>
            Yenile
          </Button>
        </div>
        
        <Search
          placeholder="Yazan kişi, başlık veya içerikte ara..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: '16px' }}
          prefix={<SearchOutlined />}
        />
      </Card>

      {filteredReferences.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={searchText ? "Arama kriterlerinize uygun referans bulunamadı" : "Henüz size yazılmış bir referans bulunmuyor"}
          style={{ marginTop: '50px' }}
        />
      ) : (
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredReferences}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} referans`,
          }}
        />
      )}

      <Modal
        title="Referans Detayı"
        open={detailModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Kapat
          </Button>
        ]}
        width={700}
        centered
      >
        {selectedReference && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <Text strong style={{ fontSize: '16px', color: 'var(--primary-500)' }}>
                {selectedReference.title || 'Başlık Yok'}
              </Text>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Yazan: </Text>
              <Text>{selectedReference.writerName}</Text>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Puan: </Text>
              <Space>
                <Rate disabled value={selectedReference.rating} style={{ fontSize: '16px' }} />
                <Text style={{ color: getRatingColor(selectedReference.rating), fontWeight: 'bold' }}>
                  {selectedReference.rating}/5
                </Text>
              </Space>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Tip: </Text>
              <Tag color={getTypeColor(selectedReference.mentorBookingId, selectedReference.bootcampApplicationId)}>
                {getTypeText(selectedReference.mentorBookingId, selectedReference.bootcampApplicationId)}
              </Tag>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Tarih: </Text>
              <Text>{dayjs(selectedReference.createdAt).format('DD MMMM YYYY, HH:mm')}</Text>
            </div>

            <Divider />

            <div>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                Referans İçeriği:
              </Text>
              <Paragraph style={{ 
                background: 'var(--bg-tertiary)', 
                padding: '16px', 
                borderRadius: '8px',
                whiteSpace: 'pre-wrap',
                marginBottom: 0
              }}>
                {selectedReference.content}
              </Paragraph>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReceivedReferencesTab;
